import React, { useState, useEffect } from 'react';
import { Dialog } from '@mui/material';
import { useModal } from '../../../contexts/ModalContext';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { numberToWords } from '../../../utils/helper';
import AgentSelect from '../../../components/common/AgentSelect';
import { fmt } from '../bms_helper';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg:      '#f2f6f3',
  white:   '#ffffff',
  ink:     '#0c1810',
  ink2:    '#1e3328',
  subtle:  '#4d6b58',
  muted:   '#8ba898',
  border:  '#dae6df',
  borderL: '#eaf2ec',
  green:   '#0a6b3e',
  greenMd: '#0d8a50',
  greenLt: '#e4f5ec',
  red:     '#dc2626',
  redLt:   '#fee2e2',
  redBdr:  '#fca5a5',
  amber:   '#d97706',
  amberLt: '#fef3c7',
  amberBdr:'#fcd34d',
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, required, children, helper, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.subtle,
        letterSpacing: '0.2px', display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {label}
        {required && <span style={{ color: T.red }}>*</span>}
      </div>
      {children}
      {error  && <div style={{ fontSize: 11, color: T.red, fontWeight: 600, marginTop: 1 }}>{error}</div>}
      {helper && !error && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{helper}</div>}
    </div>
  );
}

// ─── Focused input ────────────────────────────────────────────────────────────
function Input({ name, value, onChange, type = 'text', placeholder, min, step, suffix, hasError }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        name={name} value={value} onChange={onChange}
        type={type} placeholder={placeholder}
        min={min} step={step}
        onFocus={() => setFocused(true)}
        onBlur={()  => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: suffix ? '9px 46px 9px 12px' : '9px 12px',
          fontSize: 13, color: T.ink, background: T.white,
          border: `1.5px solid ${hasError ? T.red : focused ? T.green : T.border}`,
          borderRadius: 9, outline: 'none',
          transition: 'border-color 150ms, box-shadow 150ms',
          boxShadow: hasError
            ? '0 0 0 3px rgba(220,38,38,0.08)'
            : focused ? '0 0 0 3px rgba(10,107,62,0.1)' : 'none',
          fontFamily: 'inherit',
          MozAppearance: 'textfield',
        }}
      />
      {suffix && (
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, fontWeight: 700, color: hasError ? T.red : T.green, pointerEvents: 'none',
        }}>{suffix}</span>
      )}
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  return (
    <div style={{
      background: T.white, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.ink, letterSpacing: '-0.2px' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const AddAgentIncentive = ({ open, onClose, booking, onSuccess, agents }) => {
  const { openErrorModal }                                    = useModal();
  const { addBookingAgentIncentive, fetchBookingAgentIncentives } = useBookingDetails();

  const [loading, setLoading]                   = useState(false);
  const [existingIncentives, setExistingIncentives] = useState([]);
  const [formData, setFormData]                 = useState({ agent_id: '', amount: '', description: '' });
  const [formErrors, setFormErrors]             = useState({});

  // ── Fetch existing incentives on open ─────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setFormData({ agent_id: '', amount: '', description: '' });
    setFormErrors({});
    (async () => {
      try {
        if (booking?.id) {
          const data = await fetchBookingAgentIncentives(booking.id);
          setExistingIncentives(data || []);
        }
      } catch {
        openErrorModal('Error', 'Failed to load incentive data');
      }
    })();
  }, [open, booking?.id]);

  // ── Derived commission values ─────────────────────────────────────────────
  const cb              = booking?.commission_breakdown || {};
  const totalCommission = Number(cb.total_commission)  || 0;
  const totalExisting   = existingIncentives.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const newAmount       = parseFloat(formData.amount) || 0;
  const remaining       = totalCommission - totalExisting - newAmount;
  const isOverLimit     = remaining < 0;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (formErrors[name]) setFormErrors(p => ({ ...p, [name]: '' }));
  };

  const handleAgentChange = (agentId) => {
    setFormData(p => ({ ...p, agent_id: agentId }));
    if (formErrors.agent_id) setFormErrors(p => ({ ...p, agent_id: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.agent_id) errs.agent_id = 'Agent is required';
    if (!formData.amount) {
      errs.amount = 'Amount is required';
    } else {
      const v = parseFloat(formData.amount);
      if (isNaN(v) || v <= 0) errs.amount = 'Amount must be greater than 0';
      else if (isOverLimit)   errs.amount = `Exceeds remaining commission. Available: ₹${fmt(totalCommission - totalExisting)}`;
    }
    if (!formData.description.trim()) errs.description = 'Description is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await addBookingAgentIncentive(booking.id, {
        agent_id:    formData.agent_id,
        amount:      parseFloat(formData.amount),
        description: formData.description.trim(),
      });
      setFormData({ agent_id: '', amount: '', description: '' });
      setFormErrors({});
      onClose();
      onSuccess?.();
    } catch (err) {
      openErrorModal('Error', err.message || 'Failed to add agent incentive');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ agent_id: '', amount: '', description: '' });
    setFormErrors({});
    onClose();
  };

  const canSubmit = formData.agent_id && formData.amount && formData.description.trim() && !loading && !isOverLimit;

  return (
    <>
      <style>{`
        @keyframes aaiSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .aai-body { animation: aaiSlideUp 220ms ease both; }
        .aai-body::-webkit-scrollbar       { width: 5px; }
        .aai-body::-webkit-scrollbar-thumb { background: #ccddd5; border-radius: 99px; }
        .aai-body::-webkit-scrollbar-track { background: transparent; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .aai-textarea:focus { outline: none; border-color: ${T.green} !important; box-shadow: 0 0 0 3px rgba(10,107,62,0.1) !important; }
      `}</style>

      <Dialog
        open={open} onClose={handleClose} maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '16px', overflow: 'hidden', m: 0,
            width: '100%', maxWidth: 520, maxHeight: '92vh',
            boxShadow: '0 24px 80px rgba(0,0,0,0.26)',
            border: `1.5px solid ${T.border}`,
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: '92vh' }}>

          {/* ── Header ───────────────────────────────────────────────── */}
          <div style={{
            background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
            padding: '18px 22px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              }}>🎁</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
                  Add Agent Incentive
                </div>
                {booking && (
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                    Booking {booking.booking_id}
                  </div>
                )}
              </div>
            </div>
            <button onClick={handleClose} style={{
              background: 'rgba(255,255,255,0.14)', border: 'none',
              borderRadius: 8, width: 32, height: 32, fontSize: 18,
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          </div>

          {/* ── Commission summary strip ──────────────────────────────── */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            borderBottom: `1px solid ${T.border}`, flexShrink: 0,
          }}>
            {[
              { label: 'Total Commission', value: `₹${fmt(totalCommission)}` },
              { label: 'Already Assigned', value: `₹${fmt(totalExisting)}` },
              {
                label: 'Available',
                value: `₹${fmt(Math.max(0, totalCommission - totalExisting))}`,
                warn: (totalCommission - totalExisting) <= 0,
              },
            ].map((s, i, arr) => (
              <div key={s.label} style={{
                padding: '10px 14px',
                borderRight: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
                background: s.warn ? T.redLt : T.white,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: s.warn ? T.red : T.ink, marginTop: 2 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* ── Scrollable body ──────────────────────────────────────── */}
          <div
            className="aai-body"
            style={{
              flex: 1, minHeight: 0, overflowY: 'auto',
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
              background: T.bg,
            }}
          >

            {/* 1 — Agent & Amount */}
            <Section icon="👤" title="Incentive Details">
              <Field label="Agent" required error={formErrors.agent_id}>
                <AgentSelect
                  value={formData.agent_id}
                  onChange={handleAgentChange}
                  agents={agents}
                  error={!!formErrors.agent_id}
                  fullWidth
                />
              </Field>

              <Field
                label="Incentive Amount" required
                error={formErrors.amount}
                helper={formData.amount && !formErrors.amount
                  ? `${numberToWords(parseInt(formData.amount))} rupees only`
                  : undefined}
              >
                <Input
                  name="amount" value={formData.amount} onChange={handleChange}
                  type="number" placeholder="Enter amount"
                  min={0} step={1} suffix="₹"
                  hasError={!!formErrors.amount}
                />
              </Field>
            </Section>

            {/* Remaining commission indicator — only show when amount is entered */}
            {formData.amount && (
              <div style={{
                background: isOverLimit ? T.redLt : T.greenLt,
                border: `1.5px solid ${isOverLimit ? T.redBdr : T.border}`,
                borderRadius: 10, padding: '10px 14px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: isOverLimit ? T.red : T.subtle }}>
                  {isOverLimit ? '⚠️ Exceeds available commission' : '✓ Remaining after this incentive'}
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: isOverLimit ? T.red : T.green }}>
                  ₹{fmt(Math.abs(remaining))}
                </span>
              </div>
            )}

            {/* 2 — Description */}
            <Section icon="📝" title="Description">
              <Field
                label="Description" required
                error={formErrors.description}
                helper={!formErrors.description ? 'Describe the incentive arrangement' : undefined}
              >
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="e.g. Performance bonus for closing the deal, Referral incentive…"
                  rows={3}
                  className="aai-textarea"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', fontSize: 13, color: T.ink,
                    background: T.white,
                    border: `1.5px solid ${formErrors.description ? T.red : T.border}`,
                    borderRadius: 9, resize: 'vertical', lineHeight: 1.65,
                    transition: 'border-color 150ms', fontFamily: 'inherit',
                  }}
                />
              </Field>
            </Section>

            {/* 3 — Existing incentives (if any) */}
            {existingIncentives.length > 0 && (
              <Section icon="📋" title="Existing Incentives">
                <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                  {existingIncentives.map((inc, i) => (
                    <div key={i} style={{
                      display: 'grid', gridTemplateColumns: '1fr auto',
                      alignItems: 'center', padding: '10px 14px', gap: 12,
                      borderBottom: i < existingIncentives.length - 1 ? `1px solid ${T.borderL}` : 'none',
                      background: T.white,
                    }}>
                      <div>
                        <div style={{ fontSize: 12.5, color: T.ink2, fontWeight: 600 }}>
                          {inc.agent_name || inc.agent?.name || 'Agent'}
                        </div>
                        {inc.description && (
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{inc.description}</div>
                        )}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.green, whiteSpace: 'nowrap' }}>
                        ₹{fmt(inc.amount)}
                      </span>
                    </div>
                  ))}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr auto',
                    padding: '10px 14px', gap: 12,
                    background: T.greenLt, borderTop: `1px solid ${T.border}`,
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.green, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Assigned</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.green }}>₹{fmt(totalExisting)}</span>
                  </div>
                </div>
              </Section>
            )}

          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div style={{
            padding: '13px 18px', flexShrink: 0,
            borderTop: `1.5px solid ${T.border}`, background: '#f8fbf9',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
          }}>
            <button
              onClick={handleClose} disabled={loading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                borderRadius: 9, padding: '8px 16px',
                fontSize: 12.5, fontWeight: 700, color: T.red,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >✕ Cancel</button>

            <button
              onClick={handleSubmit} disabled={!canSubmit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: !canSubmit
                  ? '#7ab898'
                  : 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
                border: 'none', borderRadius: 9, padding: '8px 20px',
                fontSize: 12.5, fontWeight: 700, color: '#fff',
                cursor: !canSubmit ? 'not-allowed' : 'pointer',
                boxShadow: !canSubmit ? 'none' : '0 2px 10px rgba(10,107,62,0.28)',
                transition: 'all 150ms', fontFamily: 'inherit',
              }}
            >
              {loading ? '⏳ Saving…' : '🎁 Add Incentive'}
            </button>
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default AddAgentIncentive;
